import { CalendarDate } from '@internationalized/date';
import {
  BookingSortOption,
  BookingStatus,
} from '@rematter/frontend-lib-api-client';

import { BookingRecord } from '../types/booking';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';
import {
  maybeParseCalendarDate,
  parseZonedDateTime,
} from '~/shared/utils/dateTime';

export const bookingsListQueryAtom = createQueryAtomFactory(
  'trade.bookingsList',
  (params: {
    ids?: string[];
    status?: BookingStatus[];
    bookingNumber?: string;
    fromCutOffDate?: CalendarDate;
    toCutOffDate?: CalendarDate;
    fromDepartureDate?: CalendarDate;
    toDepartureDate?: CalendarDate;
    searchTerm?: string;
    limit?: number;
    offset?: number;
    sorting?: BookingSortOption[];
  }) => {
    // Clean filter object - remove undefined values
    const filter: Record<string, unknown> = {};

    if (params.ids !== undefined) filter.ids = params.ids;
    if (params.status !== undefined) filter.status = params.status;
    if (params.bookingNumber !== undefined)
      filter.documentNumber = params.bookingNumber;
    if (params.fromCutOffDate !== undefined)
      filter.fromCutOffDate = params.fromCutOffDate.toString();
    if (params.toCutOffDate !== undefined)
      filter.toCutOffDate = params.toCutOffDate.toString();
    if (params.fromDepartureDate !== undefined)
      filter.fromDepartureDate = params.fromDepartureDate.toString();
    if (params.toDepartureDate !== undefined)
      filter.toDepartureDate = params.toDepartureDate.toString();
    if (params.searchTerm !== undefined) filter.searchTerm = params.searchTerm;

    return {
      bookings: {
        __args: {
          filter,
          sorting: params.sorting || [],
          pagination: {
            limit: params.limit ?? 10,
            offset: params.offset ?? 0,
          },
        },
        entries: {
          id: true,
          facilityId: true,
          documentNumber: true,
          status: true,
          cutOffDate: true,
          earlyReturnDate: true,
          vesselName: true,
          destinationPort: {
            id: true,
            name: true,
            unLocode: true,
            countryCode: true,
          },
          containers: {
            containerType: {
              id: true,
              name: true,
            },
            quantity: true,
          },
          timestamps: {
            createdAt: true,
            updatedAt: true,
          },
          salesOrders: {
            id: true,
            documentNumber: true,
            counterpartyReferenceNumber: true,
          },
        },
        page: {
          total: true,
        },
      },
    };
  },
  {
    select: (data): { bookings: BookingRecord[]; total?: number } => {
      if (!data?.bookings.entries.length) {
        return { bookings: [], total: 0 };
      }

      return {
        bookings: data.bookings.entries.map((booking) => ({
          id: booking.id,
          facilityId: booking.facilityId,
          bookingNumber: booking.documentNumber,
          status: booking.status,
          cutOffDate: maybeParseCalendarDate(booking.cutOffDate),
          earlyReturnDate: maybeParseCalendarDate(booking.earlyReturnDate),
          vesselArrivalDate: undefined,
          vesselDepartureDate: undefined,
          vesselName: booking.vesselName ?? undefined,
          loadingPortId: undefined,
          loadingPort: undefined,
          pickupTerminalId: undefined,
          pickupTerminal: undefined,
          destinationPortId: booking.destinationPort?.id ?? undefined,
          destinationPort: booking.destinationPort ?? undefined,
          dropoffTerminalId: undefined,
          dropoffTerminal: undefined,
          steamshipLine: undefined,
          oceanFreight: undefined,
          internalNotes: undefined,
          externalNotes: undefined,
          containers:
            booking.containers?.map((container) => ({
              containerType: {
                id: container.containerType.id,
                name: container.containerType.name,
              },
              quantity: container.quantity,
            })) ?? [],
          photoRequirements: [],
          files: [],
          timestamps: {
            createdAt: parseZonedDateTime(booking.timestamps.createdAt),
            updatedAt: parseZonedDateTime(booking.timestamps.updatedAt),
          },
          salesOrders: booking.salesOrders.map((salesOrder) => ({
            id: salesOrder.id,
            documentNumber: salesOrder.documentNumber,
            counterpartyReferenceNumber:
              salesOrder.counterpartyReferenceNumber ?? undefined,
          })),
        })),
        total: data.bookings.page.total ?? 0,
      };
    },
  },
);
